import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useToast } from '@/stores/pure/ToastStore';

import { useUpdateSharedLinkMutation } from '../api/mutations';
import { calculateExpireSeconds } from '../lib/expire-utils';
import type { SharedLinkFormDefaults } from '../model/schema';

type FormStore = {
  state: { values: SharedLinkFormDefaults };
};

type FormWithStore = {
  store: FormStore;
};

export function useSharedLinkUpdate(form: FormWithStore) {
  const { workspace_id } = useParams();
  const addToast = useToast();
  const updateMutation = useUpdateSharedLinkMutation();

  const saveChanges = useCallback(async () => {
    if (!workspace_id) return;

    const values = form.store.state.values;
    const privateTargetLink = values.targetLink;
    const privateTargetUrl = new URL(privateTargetLink);
    const publicTargetLink = `${window.location.origin}/share${privateTargetUrl.pathname}${privateTargetUrl.search}${privateTargetUrl.hash}`;

    const privateLinkSave = async () => {
      await updateMutation.mutateAsync({
        linkId: values.sharedLinkId,
        link: privateTargetLink,
      });
    };

    const publicLinkSave = async () => {
      const expire = calculateExpireSeconds(
        values.linkExpireMode,
        values.linkExpireEnabled,
      );

      const password = values.passwordEnabled
        ? values.password || undefined
        : undefined;

      await updateMutation.mutateAsync({
        linkId: values.sharedLinkId,
        link: publicTargetLink,
        expire,
        password,
      });
    };

    try {
      const isPublic = values.isPublic;

      if (isPublic) {
        await publicLinkSave();
      } else {
        await privateLinkSave();
      }

      addToast('Settings updated', 'success');
    } catch (error) {
      console.error('Failed to update shared link:', error);
      addToast('Failed to update settings', 'fail');
    }
  }, [form, workspace_id, addToast, updateMutation]);

  return { saveChanges };
}
